import type { OptionNavigatorProps } from './option-navigator.props';
import { useEffect, useRef } from 'react';
import { ScrollElement } from '../../common/scroll-element/scroll-element.component';
import type { JsonData } from '../../generic-types';
import type { DropDownResult } from '../../fields/drop-down/drop-down-types';
import dot from 'dot-object';
import { useSignalEffect } from '@preact/signals-react';
import type { TypeOrArray } from '../../common-interfaces';
import { useEvent, useEventThrottle } from '../../../common/hooks/use-event';

const OptionNavigator = ({
  valueField,
  textField,
  archivedField,
  itemsToRemove,
  registerOnFocus = true,
  registerSignal,
  removeOnSelect = false,
  selectedValue,
  scrollElementCreated,
  keyDown,
  highlightedValueChanged,
  modifierKeysChanged,
  dataChanged,
  pageDataLoaded,
  change,
  readonly = false,
  className,
  children,
}: OptionNavigatorProps) => {
  const isFocused = useRef(false);
  const isShiftDown = useRef(false);
  const isCtrlCmdDown = useRef(false);
  const currentDataLength = useRef(0);
  const highlightedIndex = useRef<number | undefined>(undefined);

  const currentItemData = useRef<JsonData | undefined>(undefined);
  const firstValue = useRef<DropDownResult | undefined>(undefined);
  const selectedHighlightedValue = useRef<DropDownResult | undefined>(undefined);
  const selectedValueRef = useRef<Array<DropDownResult> | undefined>(undefined);

  useEffect(() => {
    return () => {
      // cleanup the event listeners
      document.removeEventListener('keydown', onModifierKeyDown);
      document.removeEventListener('keyup', onModifierKeyUp);

      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only cleanup: handlers are stable (from useEvent)
  }, []);

  useEffect(() => {
    selectedValueRef.current = selectedValue;
  }, [selectedValue]);

  useSignalEffect(() => {
    if (!registerOnFocus && registerSignal) {
      if (registerSignal.value) {
        if (!isFocused.current) {
          document.addEventListener('keydown', onModifierKeyDown);
          document.addEventListener('keyup', onModifierKeyUp);

          document.addEventListener('keydown', onKeyDown);
          document.addEventListener('keyup', onKeyUp);

          isFocused.current = true;
        }
      } else {
        document.removeEventListener('keydown', onModifierKeyDown);
        document.removeEventListener('keyup', onModifierKeyUp);

        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('keyup', onKeyUp);

        isFocused.current = false;
      }
    }
  });

  const onFocus = () => {
    if (!isFocused.current) {
      document.addEventListener('keydown', onModifierKeyDown);
      document.addEventListener('keyup', onModifierKeyUp);

      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('keyup', onKeyUp);

      isFocused.current = true;
    }
  };

  const onBlur = () => {
    document.removeEventListener('keydown', onModifierKeyDown);
    document.removeEventListener('keyup', onModifierKeyUp);

    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);

    isFocused.current = false;
  };

  const onModifierKeyDown = useEvent((event: KeyboardEvent) => {
    const { shiftKey, metaKey, ctrlKey } = event;

    isShiftDown.current = shiftKey;
    isCtrlCmdDown.current = metaKey || ctrlKey;
    modifierKeysChanged?.(shiftKey, metaKey, ctrlKey);
  });

  const onModifierKeyUp = useEvent((event: KeyboardEvent) => {
    const { shiftKey, metaKey, ctrlKey } = event;

    isShiftDown.current = shiftKey;
    isCtrlCmdDown.current = metaKey || ctrlKey;
    modifierKeysChanged?.(shiftKey, metaKey, ctrlKey);
  });

  const setHighlightedItem = () => {
    const selectedHighlightedValue: DropDownResult = {
      value: dot.pick(valueField, currentItemData.current?.[highlightedIndex.current ?? 0]),
      text: dot.pick(textField, currentItemData.current?.[highlightedIndex.current ?? 0]),
    };
    highlightedValueChanged(selectedHighlightedValue, highlightedIndex.current ?? 0);
  };

  const moveHighlightedValueUp = () => {
    if (currentItemData.current && highlightedIndex.current !== undefined) {
      highlightedIndex.current--;
      const skipItems: Array<DropDownResult> = [];

      if (itemsToRemove && itemsToRemove.length > 0) {
        skipItems.push(...itemsToRemove);
      }
      if (removeOnSelect && selectedValueRef.current && selectedValueRef.current.length > 0) {
        skipItems.push(...selectedValueRef.current);
      }

      if (skipItems.length > 0) {
        let potentialHighlightedValue = dot.pick(valueField, currentItemData.current[highlightedIndex.current]);
        while (
          skipItems.findIndex((item) => item.value === potentialHighlightedValue) > -1 &&
          highlightedIndex.current > 0
        ) {
          highlightedIndex.current--;
          potentialHighlightedValue = dot.pick(valueField, currentItemData.current[highlightedIndex.current]);
        }
      }

      setHighlightedItem();
    }
  };

  const moveHighlightedValueDown = () => {
    if (currentItemData.current && highlightedIndex.current !== undefined) {
      const skipItems: Array<DropDownResult> = [];

      if (itemsToRemove && itemsToRemove.length > 0) {
        skipItems.push(...itemsToRemove);
      }
      if (removeOnSelect && selectedValueRef.current && selectedValueRef.current.length > 0) {
        skipItems.push(...selectedValueRef.current);
      }

      if (skipItems.length > 0) {
        let potentialHighlightedValue = dot.pick(valueField, currentItemData.current[highlightedIndex.current]);
        while (
          skipItems.findIndex((item) => item.value === potentialHighlightedValue) > -1 &&
          highlightedIndex.current > 0
        ) {
          highlightedIndex.current++;
          potentialHighlightedValue = dot.pick(valueField, currentItemData.current[highlightedIndex.current]);
        }
      }

      setHighlightedItem();
    }
  };

  const onKeyDown = useEventThrottle(
    (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'ArrowDown') {
        if (currentItemData.current && currentItemData.current.length > 0) {
          if (highlightedIndex.current !== undefined) {
            if (highlightedIndex.current < currentDataLength.current) {
              highlightedIndex.current++;
            }
          } else {
            highlightedIndex.current = 0;
          }

          moveHighlightedValueDown();
        }

        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'ArrowUp') {
        if (currentItemData.current && currentItemData.current.length > 0) {
          if (highlightedIndex.current !== undefined && highlightedIndex.current > 0) {
            moveHighlightedValueUp();
          }
        }

        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'Enter') {
        if (currentItemData.current && currentItemData.current.length > 0) {
          if (highlightedIndex.current !== undefined) {
            selectedHighlightedValue.current = {
              value: dot.pick(valueField, currentItemData.current[highlightedIndex.current]),
              text: dot.pick(textField, currentItemData.current[highlightedIndex.current]),
              originalData: currentItemData.current[highlightedIndex.current],
            };
          }

          keyDown(key, selectedHighlightedValue.current);
        }
      }
    },
    500,
    { leading: true }
  );

  const onKeyUp = useEvent((_event: KeyboardEvent) => onKeyDown.cancel());

  const onDataChanged = (data: JsonData) => {
    // when the available items change the first value needs to be reset
    firstValue.current = {
      text: dot.pick(textField, data[0]),
      value: dot.pick(valueField, data[0]),
      originalData: data[0],
    };

    currentItemData.current = data;
    currentDataLength.current = data.length > 0 ? data.length - 1 : 0;
    dataChanged?.(data);
  };

  const onPageDataLoaded = (page: number, pageData: JsonData) => {
    if (pageData.length > 0) {
      if (page === 1) {
        firstValue.current = {
          text: dot.pick(textField, pageData[0]),
          value: dot.pick(valueField, pageData[0]),
          archived: archivedField ? !!dot.pick(archivedField, pageData[0]) : false,
          originalData: pageData[0],
        };
        highlightedIndex.current = undefined;
      }
    }

    pageDataLoaded?.(page, pageData);
  };

  const onChange = (item: TypeOrArray<DropDownResult>) => {
    if (currentItemData.current) {
      const selectedIndex = Array.isArray(item)
        ? currentItemData.current.findIndex(
            (dataItem) => dot.pick(valueField, dataItem) === item[item.length - 1].value
          )
        : currentItemData.current.findIndex((dataItem) => dot.pick(valueField, dataItem) === item.value);
      highlightedIndex.current = selectedIndex > -1 ? selectedIndex : undefined;
    }

    change?.(item);
  };

  return (
    <ScrollElement
      focus={registerOnFocus ? onFocus : undefined}
      blur={registerOnFocus ? onBlur : undefined}
      elementCreated={scrollElementCreated}
      readonly={readonly}
      className={className}
    >
      {children?.({ onDataChanged, onPageDataLoaded, onChange })}
    </ScrollElement>
  );
};

export { OptionNavigator };
